import styled, { keyframes, css } from 'styled-components';
import { Paragraph } from '@swordhealth/ui-corporate';

export const ImageWrapper = styled.div`
  position: absolute;
  z-index: 0;
  height: 336px;
  width: 280px;
  animation-timing-function: ease-out;
  will-change: opacity, transform;

  @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
    height: 168px;
    width: 142px;
  }

  ${(props) =>
    props.$position === 0 &&
    css`
      top: 25%;
      left: -340px;
      transform: rotate(12deg);
      animation: ${fadeIn1} 1s forwards;

      @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
        top: 12%;
        left: -135px;
      }
    `};

  ${(props) =>
    props.$position === 1 &&
    css`
      top: -180px;
      right: -310px;
      transform: rotate(12deg);
      animation: ${fadeIn2} 1.3s forwards;

      @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
        right: -90px;
        top: 0;
      }
    `};

  ${(props) =>
    props.$position === 2 &&
    css`
      bottom: -150px;
      right: -330px;
      transform: rotate(-16deg);
      animation: ${fadeIn3} 1.4s forwards;

      @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
        right: -80px;
        bottom: -100px;
      }
    `};
`;

// Define the fade-in animation
const fadeIn1 = keyframes`
  0% {
   opacity: 0;
    transform: rotate(20deg) translateY(20px) translateX(-20px) scale(0.75);
  }
  100% {
    opacity: 1;
    transform: rotate(12deg) translateY(0) translateX(0) scale(1);
  }
`;

// Define the fade-in animation
const fadeIn2 = keyframes`
  0% {
    opacity: 0;
  }
  20% {
  opacity: 0;
    transform: rotate(20deg) translateY(20px) scale(0.75);
  }
  100% {
    opacity: 1;
    transform: rotate(12deg) translateY(0) scale(1);
  }
`;

// Define the fade-in animation
const fadeIn3 = keyframes`
  0% {
    opacity: 0;
  }
  30% {
  opacity: 0;
  transform: rotate(-8deg) translateY(20px) scale(0.75);
  }
  100% {
    opacity: 1;
    transform: rotate(-16deg) translateY(0) scale(1);
  }
`;

export const LogosWrapper = styled.div`
  text-align: center;
  z-index: 10;
  width: 100%;
  animation: logoFadeIn 0.3s ease-out forwards;
  margin-bottom: 20px;

  @media (max-width: ${(props) => props.theme.breakpoints.max_md}px) {
    margin-bottom: 60px;
  }

  > div {
    display: inline-flex;
  }

  @keyframes logoFadeIn {
    0% {
      opacity: 0;
      transform: scale(0.7);
    }
    100% {
      opacity: 1;
      transform: scale(1);
    }
  }
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  max-width: 640px;
  overflow: visible;
  width: 50%;
  z-index: 5;
  text-align: center;
  position: relative;

  @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
    gap: 16px;
    margin: 0 auto;
    max-width: 448px;
    width: 100%;
  }

  h1 {
    color: ${({ theme }) => theme.colors.primary.default};
  }
`;

export const BackgroundShape = styled.div`
  position: absolute;
  background: radial-gradient(
    40.79% 37.3% at 49.81% 49.38%,
    rgba(255, 255, 255, 0.6) 0%,
    rgba(255, 255, 255, 0.6) 100%
  );

  border-radius: 50%;
  z-index: -9;
  height: 100%;
  width: 100%;
  filter: blur(125px);
  backdrop-filter: blur(125px);

  @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
    top: 88px;
    height: 80%;
    width: 80%;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
    background: radial-gradient(70% 60% at 50% 50%);
    height: 60%;
    width: 60%;
    filter: blur(100px);
    backdrop-filter: blur(100px);
  }
`;

export const Subtitle = styled(Paragraph)`
  font-size: 16px;
  line-height: 28px;
  content-visibility: auto;
`;

export const ExtraLabel = styled(Subtitle)`
  color: #747986;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  max-width: 280px;

  @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
    max-width: 100%;
  }
`;

export const ButtonWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  flex-direction: row;
  column-gap: 32px;
  row-gap: 12px;

  @media (max-width: ${({ theme }) => theme.breakpoints.max_md}px) {
    flex-direction: column;
    gap: 12px;
  }
`;

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  overflow: visible;
  z-index: 5;
  text-align: center;
  position: relative;
  animation: contentFadeIn 0.6s ease-out forwards;

  & > h1 {
    font-size: 64px;
    line-height: 64px;
    color: var(--ui-colors-primary-600);
    font-family: var(--ui-font-family-gelica);

    @media (max-width: ${({ theme }) => theme.breakpoints.max_sm}px) {
      line-height: 40px;
      font-size: 40px;
    }
  }

  @keyframes contentFadeIn {
    0% {
      opacity: 0;
    }
    30% {
      opacity: 0;
      transform: translateY(-30px);
    }
    100% {
      opacity: 1;
      transform: translateY(0px);
    }
  }
`;

export const FixedWrapper = styled.div`
  width: 100%;
  height: 66px;
  background: #f7f4f2cc;
  backdrop-filter: blur(4px);
  box-shadow: 0px 16px 40px 0px #00000014;
  box-shadow: 0px 2px 4px 0px #0000000a;
  text-align: center;
  padding: 10px 0;
  position: fixed;
  top: 0;
  z-index: 10000;
  display: none;

  ${(props) =>
    props.$visible &&
    css`
      display: block;
    `};
`;
export const EnrollButtonWrapper = styled.div`
  position: relative;

  @media (max-width: ${(props) => props.theme.breakpoints.max_sm}px) {
    width: 100%;
    text-align: center;
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 16px;
  }
`;
export const EnrollContainer = styled.div`
  width: calc(100% - 96px);
`;
